import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  Club,
  Member,
  Court,
  Match,
  QueueEntry,
  Equipment,
  Evaluation,
  PlayTime,
  ApiClub,
  ApiClubMember,
  ApiQueueEntry,
  LeaderboardEntry,
} from '@/types';
import {
  clubService,
  memberService,
  queueService,
  courtService,
  userService,
  equipmentService,
  feedbackService,
  matchService,
} from '@/services/storageService';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from './AuthContext';
import { buildAvatarDataUri } from '@/lib/avatarBuilder';
import { toast } from 'sonner';
import { fetchClubsForUser } from '@/lib/clubMembership';
import { validateBadmintonFinalScore } from '@/lib/scoreValidation';
import { API_BASE as API_BASE_URL } from '@/services/apiClient';

const API_BASE = API_BASE_URL;

interface AdminAddMemberResponse {
  message: string;
  member?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    nickname?: string | null;
  };
  onboarding_token?: string;
}

interface ApiCourt {
  id: number;
  court_number: number;
  is_active?: boolean;
}

interface AssignPlayersResult {
  team1: string[];
  team2: string[];
  courtId: string;
  matchId?: string;
  roundRobinPool?: string[] | null;
  priorityCandidates?: string[] | null;
}

interface ClubContextType {
  club: Club | null;
  isLoading: boolean;
  hasLoadedInitial: boolean;
  isRefreshing: boolean;
  activeClubId: number | null;
  currentClubRole: Member['role'] | null;
  members: Member[];
  courts: Court[];
  matches: Match[];
  matchHistory: Match[];
  leaderboard: LeaderboardEntry[];
  queue: QueueEntry[];
  refreshData: (clubIdOverride?: number | null, options?: { light?: boolean }) => void;
  selectClub: (clubId: number) => Promise<void>;
  createClub: (clubData: Omit<Club, 'id' | 'createdAt' | 'adminId'>) => Promise<void>;
  updateClub: (updates: Partial<Club>) => Promise<void>;
  addMember: (
    memberData: Omit<Member, 'id' | 'createdAt' | 'equipment' | 'evaluations' | 'isAvailable' | 'isPlaying'>
  ) => Promise<AdminAddMemberResponse>;
  updateMember: (id: string, updates: Partial<Member>) => Promise<void>;
  deleteMember: (id: string) => Promise<void>;
  getMemberById: (id: string) => Member | undefined;
  getMemberByUserId: (userId: string) => Member | undefined;
  updateMemberStatus: (id: string, isActive: boolean) => Promise<void>;
  addEquipment: (memberId: string, equipment: Omit<Equipment, 'id' | 'addedAt'>) => Promise<void>;
  updateEquipment: (
    memberId: string,
    equipmentId: string,
    updates: Partial<Equipment>
  ) => Promise<void>;
  deleteEquipment: (memberId: string, equipmentId: string) => Promise<void>;
  addEvaluation: (memberId: string, evaluation: Omit<Evaluation, 'id' | 'date'>) => Promise<void>;
  deleteEvaluation: (memberId: string, evaluationId: string) => Promise<void>;
  addToQueue: (memberId: string) => Promise<void>;
  removeFromQueue: (memberId: string) => Promise<void>;
  setPlayerAvailable: (memberId: string, available: boolean) => Promise<void>;
  clearQueue: () => Promise<void>;
  shuffleQueue: () => Promise<void>;
  initializeCourts: (count: number) => Promise<void>;
  assignPlayersToCourt: (courtId: string) => Promise<AssignPlayersResult | null>;
  finishMatch: (
    courtId: string,
    score: { team1: number; team2: number },
    equipmentUsed: Match['equipmentUsed'],
    notes?: string,
    recordedBy?: string
  ) => Promise<void>;
  canManageCourts: () => boolean;
  canEvaluate: () => boolean;
}

const ClubContext = createContext<ClubContextType | undefined>(undefined);

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_NAME_TO_NUMBER = DAY_NAMES.reduce<Record<string, number>>((acc, day, index) => {
  acc[day] = index + 1;
  return acc;
}, {});
const NUMBER_TO_DAY = DAY_NAMES.reduce<Record<number, string>>((acc, day, index) => {
  acc[index + 1] = day;
  return acc;
}, {});

const mapPlaySessions = (club: ApiClub): PlayTime[] => {
  if (!club.play_sessions) return [];
  return club.play_sessions.map((session) => ({
    day: NUMBER_TO_DAY[session.day_of_week] || 'Monday',
    startTime: session.start_time_str,
    endTime: session.end_time_str,
  }));
};

const mapSelectionMode = (mechanism?: string): Club['selectionMode'] => {
  if (!mechanism) return 'round-robin';
  switch (mechanism.toUpperCase()) {
    case 'SKILL_BASED':
      return 'skill-based';
    case 'BALANCED_TEAM':
      return 'balanced';
    default:
      return 'round-robin';
  }
};

const mapClub = (apiClub: ApiClub): Club => {
  const normalizeLogo = (url?: string | null) => {
    if (!url) return undefined;
    const trimmed = url.trim();
    if (!trimmed) return undefined;

    const rebuild = (path: string) => {
      if (!API_BASE) return path;
      return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    try {
      const parsed = new URL(trimmed);
      const pathWithSearch = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      return rebuild(pathWithSearch);
    } catch {
      // Not a full URL, treat as relative
      return rebuild(trimmed);
    }
  };
  const playTimes = mapPlaySessions(apiClub);
  return {
    id: String(apiClub.id),
    name: apiClub.name,
    sportType: apiClub.sport_type || undefined,
    slogan: apiClub.slogan || undefined,
    logo: normalizeLogo(apiClub.logo_url),
    address: apiClub.address || undefined,
    playDays: playTimes.map((ps) => ps.day),
    playTimes,
    playTime: playTimes[0] ? `${playTimes[0].startTime} - ${playTimes[0].endTime}` : undefined,
    approxPlayers: apiClub.approx_num_players || undefined,
    numberOfCourts: apiClub.num_courts,
    selectionMode: mapSelectionMode(apiClub.selection_mechanism),
    adminId: apiClub.created_by_id ? String(apiClub.created_by_id) : undefined,
    createdAt: undefined,
  };
};

const mapCourts = (apiClub: ApiClub & { courts?: ApiCourt[] }): Court[] => {
  if (Array.isArray(apiClub.courts) && apiClub.courts.length) {
    return apiClub.courts.map((court) => ({
      id: String(court.id),
      number: court.court_number,
      players: [],
      team1: [],
      team2: [],
      isActive: false,
    }));
  }

  return Array.from({ length: apiClub.num_courts || 0 }, (_, idx) => ({
    id: `virtual-${idx + 1}`,
    number: idx + 1,
    players: [],
    team1: [],
    team2: [],
    isActive: false,
  }));
};

const clampSkill = (val: number | null | undefined, fallback: number) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return fallback;
  return Math.max(1, Math.min(5, Number(val)));
};

const mapMember = (apiMember: ApiClubMember): Member => {
  const role = apiMember.role.toLowerCase() as Member['role'];
  const skillLevel = role === 'coach' ? 5 : undefined;

  return {
    id: String(apiMember.member_id),
    userId: String(apiMember.member_id),
    firstName: apiMember.first_name,
    lastName: apiMember.last_name,
    email: apiMember.email,
    nickName: apiMember.nickname || undefined,
    role,
    originalRole: apiMember.role,
    skillLevel,
    hasEvaluation: role === 'coach',
    equipment: [],
    evaluations: [],
    isAvailable: false,
    isPlaying: false,
    isActive: apiMember.is_active !== false,
    avatarUrl: buildAvatarDataUri(apiMember.collection_name, apiMember.avatar_seed) || undefined,
    avatarSeed: apiMember.avatar_seed || undefined,
    avatarCollection: apiMember.collection_name || undefined,
    avatarName: apiMember.avatar_name || undefined,
  };
};

const mapQueueEntry = (entry: ApiQueueEntry): QueueEntry => ({
  userId: String(entry.member_id),
  firstName: entry.first_name,
  lastName: entry.last_name,
  position: entry.position,
  status: entry.status,
  waitStartedAt: entry.wait_started_at || null,
  elapsedSeconds: typeof entry.elapsed_seconds === 'number' ? entry.elapsed_seconds : null,
  lastQueueSeconds: typeof entry.last_queue_seconds === 'number' ? entry.last_queue_seconds : null,
  priorityCandidate: Boolean(entry.is_priority_candidate),
});

const mapShoesToEquipment = (items: any[]): Equipment[] =>
  (items || []).map((s) => ({
    id: String(s.id),
    type: 'shoes',
    name: s.make,
    make: s.make,
    manufacturer: s.manufacturer || '',
    addedAt: s.created_at || new Date().toISOString(),
  }));

const mapRacketsToEquipment = (items: any[]): Equipment[] =>
  (items || []).map((r) => ({
    id: String(r.id),
    type: 'racket',
    name: r.make,
    make: r.make,
    manufacturer: r.manufacturer || '',
    tension: r.tension ? parseInt(r.tension, 10) || undefined : undefined,
    stringType: r.string_type || undefined,
    addedAt: r.created_at || new Date().toISOString(),
  }));

const buildPlayerNameMap = (ids: Array<string | number>, memberList: Member[]) => {
  const map: Record<string, { id: string; firstName?: string; lastName?: string; role?: string }> = {};
  ids.forEach((pid) => {
    const key = String(pid);
    const member = memberList.find((m) => m.id === key);
    if (member) {
      map[key] = {
        id: key,
        firstName: member.firstName,
        lastName: member.lastName,
        role: member.role,
      };
    }
  });
  return map;
};

const mapEvaluations = (items: any[]): Evaluation[] => {
  const toNumber = (value: unknown) => {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : 0;
  };
  return (items || []).map((f) => {
    const categories = {
      technical: f.technical_skills ?? undefined,
      movement: f.movement ?? undefined,
      tactical: f.tactical_ability ?? undefined,
      physical: f.physical_attributes ?? undefined,
      psychological: f.psychological_factors ?? undefined,
    };
    return {
      id: String(f.id),
      coachId: f.coach_id ? String(f.coach_id) : '',
      coachName: f.coach_name,
      rating: toNumber(f.average_rating),
      feedback: typeof f.comments === 'string' ? f.comments : '',
      quarterPlan: f.quarter_plan || f.comments || '',
      categories,
      date: f.created_at,
      strengths: '',
      areasToImprove: '',
    };
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export function ClubProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [club, setClub] = useState<Club | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchHistory, setMatchHistory] = useState<Match[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [activeClubId, setActiveClubId] = useState<number | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const bootstrapClubRef = useRef(false);
  const lastFetchedClubRef = useRef<number | null>(null);
  const preferredKey = user ? `preferredClubId:${user.id}` : 'preferredClubId';

  const refreshData = useCallback(async (clubIdOverride?: number | null, options?: { light?: boolean }) => {
    const light = options?.light;
    const isInitialLoad = !hasLoadedInitial;
    if (isInitialLoad) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    if (!user) {
      setIsLoading(false);
      setIsRefreshing(false);
      return;
    }
    if (user.needsConsent) {
      setClub(null);
      setMembers([]);
      setCourts([]);
      setMatches([]);
      setMatchHistory([]);
      setLeaderboard([]);
      setQueue([]);
      setActiveClubId(null);
      setIsLoading(false);
      setIsRefreshing(false);
      setHasLoadedInitial(true);
      return;
    }
    if (user.isSuperAdmin) {
      setClub(null);
      setMembers([]);
      setCourts([]);
      setMatches([]);
      setMatchHistory([]);
      setLeaderboard([]);
      setQueue([]);
      setActiveClubId(null);
      setIsLoading(false);
      setIsRefreshing(false);
      setHasLoadedInitial(true);
      return;
    }
    try {
      const rawClubs = user
        ? await fetchClubsForUser(user.id)
        : (await clubService.list()) as ApiClub[];

      const clubsResponse = rawClubs || [];

      if (!Array.isArray(clubsResponse) || clubsResponse.length === 0) {
        // If admin has no clubs, keep empty state (they can create one in dashboard/club)
        setClub(null);
        setMembers([]);
        setCourts([]);
        setMatches([]);
        setMatchHistory([]);
        setLeaderboard([]);
        setQueue([]);
        setActiveClubId(null);
        setIsLoading(false);
        setIsRefreshing(false);
        setHasLoadedInitial(true);
        return;
      }

      const storedPreferred = Number(localStorage.getItem(preferredKey) || 0) || null;
      const availableIds = new Set(clubsResponse.map((c) => Number(c.id)));
      const preferredClubId =
        storedPreferred && availableIds.has(storedPreferred) ? storedPreferred : null;

      const currentClubId =
        clubIdOverride && availableIds.has(Number(clubIdOverride))
          ? clubIdOverride
          : activeClubId && availableIds.has(Number(activeClubId))
            ? activeClubId
            : preferredClubId || clubsResponse[0].id;

      if (currentClubId !== activeClubId) {
        setActiveClubId(currentClubId);
        localStorage.setItem(preferredKey, String(currentClubId));
      }

      const [clubDetails, memberList, queueList, clubHistory, clubLeaderboard] = await Promise.all([
        clubService.get(currentClubId) as Promise<ApiClub & { courts?: ApiCourt[] }>,
        memberService.list(currentClubId) as Promise<ApiClubMember[]>,
        queueService.list(currentClubId) as Promise<ApiQueueEntry[]>,
        matchService.listHistoryByClub(currentClubId) as Promise<any[]>,
        matchService.getLeaderboard(currentClubId).catch(() => ({ items: [] })) as Promise<any>,
      ]);

      const mappedClub = mapClub(clubDetails);
      setClub(mappedClub);
      const baseCourts = mapCourts(clubDetails);

      const queueEntries = (queueList as ApiQueueEntry[]).map(mapQueueEntry);
      setQueue(queueEntries);
      const queuedIds = new Set(queueEntries.filter((q) => q.status === 'QUEUED').map((entry) => entry.userId));
      const playingIds = new Set(queueEntries.filter((q) => q.status === 'PLAYING').map((entry) => entry.userId));

      // Build member models after queue state updates
      const memberModels = (memberList as ApiClubMember[]).map((member) => {
        const mapped = mapMember(member);
        mapped.isAvailable = queuedIds.has(mapped.id);
        mapped.isPlaying = playingIds.has(mapped.id);
        return mapped;
      });
      // Ensure admin (current user) appears in members list if they are not present
      // Enrich members with evaluations for this club (batched for admins/coaches)
      const currentMember = user
        ? memberModels.find(
            (m) =>
              m.id === String(user.id) ||
              m.userId === String(user.id) ||
              (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()),
          )
        : undefined;
      const isAdmin = currentMember?.role === 'admin';
      const isCoach = currentMember?.role === 'coach';
      let evalsByMember: Record<string, Evaluation[]> = {};
      if (!light && (isAdmin || isCoach)) {
        const evalsRes = await feedbackService.listForClub(currentClubId);
        const grouped: Record<string, any[]> = {};
        (evalsRes as any[]).forEach((item) => {
          const memberId = String((item as any).player_id ?? (item as any).playerId ?? '');
          if (!memberId) return;
          if (!grouped[memberId]) grouped[memberId] = [];
          grouped[memberId].push(item);
        });
        evalsByMember = Object.fromEntries(
          Object.entries(grouped).map(([id, items]) => [id, mapEvaluations(items as any[])]),
        );
      }

      const enrichedMembers = memberModels.map((member) => {
        const evalList = !light && (isAdmin || isCoach) ? evalsByMember[member.id] || [] : [];
        return {
          ...member,
          evaluations: evalList,
          hasEvaluation: member.hasEvaluation || evalList.length > 0,
        };
      });

      const finalMembers = [...enrichedMembers];
      const ownsClub = Boolean(mappedClub && mappedClub.adminId && user && String(mappedClub.adminId) === String(user.id));
      if (ownsClub && !finalMembers.some((m) => m.userId === user.id)) {
        finalMembers.unshift({
          id: user.id,
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: 'admin',
          originalRole: 'ADMIN',
          skillLevel: undefined,
          hasEvaluation: false,
          equipment: [],
          evaluations: [],
          isAvailable: false,
          isPlaying: false,
          isActive: true,
        });
      }

      setMembers(finalMembers);
      // Map match history for this club (includes ongoing)
      const mappedHistory: Match[] = Array.isArray(clubHistory)
        ? clubHistory.map((item: any) => {
          const status = String(item.status || (item.finished_at ? 'finished' : 'ongoing')).toLowerCase() as
            | 'ongoing'
            | 'finished';
          const players = Array.isArray(item.players) ? item.players.map((p: any) => String(p)) : [];
          const team1 = Array.isArray(item.team1) ? item.team1.map((p: any) => String(p)) : [];
          const team2 = Array.isArray(item.team2) ? item.team2.map((p: any) => String(p)) : [];
          const recordedByRaw = item.recordedBy ?? item.recorded_by;
          const recordedBy = recordedByRaw ? String(recordedByRaw) : '';
          const recordedByName = item.recordedByName || item.recorded_by_name;
          const recordedByRoleRaw = item.recordedByRole || item.recorded_by_role;
          const recordedByRole = recordedByRoleRaw ? String(recordedByRoleRaw).toLowerCase() : undefined;

          const assignedById = item.assignedById || item.assigned_by_id;
          const assignedByMemberId = item.assignedByMemberId || item.assigned_by_member_id;
          const assignedByName = item.assignedByName || item.assigned_by_name;
          const assignedByRoleRaw = item.assignedByRole || item.assigned_by_role;
          const assignedByRole = assignedByRoleRaw ? String(assignedByRoleRaw).toLowerCase() : undefined;

          const playerNamesFromApi = item.player_names || {};
          const playerNames = {
            ...Object.fromEntries(Object.entries(playerNamesFromApi).map(([k, v]: any) => [String(k), {
              id: String(k),
              firstName: v?.first_name,
              lastName: v?.last_name,
              role: v?.role ? String(v.role).toLowerCase() : undefined,
            }])),
            ...buildPlayerNameMap([...new Set([...team1, ...team2, ...players])], finalMembers),
          };
          const addExtraPerson = (id?: string | number, name?: string, role?: string) => {
            const key = id ? String(id) : '';
            if (!key || playerNames[key]) return;
            const parts = (name || '').trim().split(/\s+/);
            const firstName = parts[0] || undefined;
            const lastName = parts.slice(1).join(' ') || undefined;
            playerNames[key] = {
              id: key,
              firstName,
              lastName,
              role: role ? String(role).toLowerCase() : undefined,
            };
          };
          addExtraPerson(recordedBy, recordedByName, recordedByRole);
          addExtraPerson(assignedByMemberId || assignedById, assignedByName, assignedByRole);
          return {
            id: String(item.id || item.match_id || crypto.randomUUID()),
            courtNumber: item.courtNumber || item.court_number || 0,
            courtId: item.courtId ? String(item.courtId) : item.court_id ? String(item.court_id) : undefined,
            status,
            players,
            team1,
            team2,
            score: {
              team1: Number((item.score && item.score.team1) ?? item.score_team1 ?? 0) || 0,
              team2: Number((item.score && item.score.team2) ?? item.score_team2 ?? 0) || 0,
            },
            timestamp: item.timestamp || item.finished_at || item.started_at || new Date().toISOString(),
            startedAt: item.started_at || item.startedAt || item.timestamp,
            finishedAt: item.finished_at || item.finishedAt,
            durationSeconds: item.durationSeconds || item.duration_seconds,
            recordedBy,
            recordedByName,
            recordedByRole,
            assignedById,
            assignedByMemberId,
            assignedByName,
            assignedByRole,
            notes: typeof item.notes === 'string' && item.notes.trim() ? item.notes : undefined,
            equipmentUsed: Array.isArray(item.equipmentUsed || item.equipment_used)
              ? (item.equipmentUsed || item.equipment_used).map((eq: any) => ({
                memberId: String(eq.memberId || eq.member_id || ''),
                shoes: eq.shoes,
                racket: eq.racket,
              }))
              : [],
            isActive: status === 'ongoing',
            playerNames,
          };
        })
        : [];
      let historyForUi = mappedHistory;
      if (!isAdmin && !isCoach && user) {
        try {
          const memberHistory = await matchService.listHistoryForUserInClub(user.id, currentClubId);
          const mappedMemberHistory: Match[] = Array.isArray(memberHistory)
            ? memberHistory.map((item: any) => {
              const status = String(item.status || (item.finished_at ? 'finished' : 'ongoing')).toLowerCase() as
                | 'ongoing'
                | 'finished';
              const players = Array.isArray(item.players) ? item.players.map((p: any) => String(p)) : [];
              const team1 = Array.isArray(item.team1) ? item.team1.map((p: any) => String(p)) : [];
              const team2 = Array.isArray(item.team2) ? item.team2.map((p: any) => String(p)) : [];
              const recordedByRaw = item.recordedBy ?? item.recorded_by;
              const recordedBy = recordedByRaw ? String(recordedByRaw) : '';
              const recordedByName = item.recordedByName || item.recorded_by_name;
              const recordedByRoleRaw = item.recordedByRole || item.recorded_by_role;
              const recordedByRole = recordedByRoleRaw ? String(recordedByRoleRaw).toLowerCase() : undefined;

              const assignedById = item.assignedById || item.assigned_by_id;
              const assignedByMemberId = item.assignedByMemberId || item.assigned_by_member_id;
              const assignedByName = item.assignedByName || item.assigned_by_name;
              const assignedByRoleRaw = item.assignedByRole || item.assigned_by_role;
              const assignedByRole = assignedByRoleRaw ? String(assignedByRoleRaw).toLowerCase() : undefined;

              const playerNamesFromApi = item.player_names || {};
              const playerNames = {
                ...Object.fromEntries(Object.entries(playerNamesFromApi).map(([k, v]: any) => [String(k), {
                  id: String(k),
                  firstName: v?.first_name,
                  lastName: v?.last_name,
                  role: v?.role ? String(v.role).toLowerCase() : undefined,
                }])),
                ...buildPlayerNameMap([...new Set([...team1, ...team2, ...players])], finalMembers),
              };
              const addExtraPerson = (id?: string | number, name?: string, role?: string) => {
                const key = id ? String(id) : '';
                if (!key || playerNames[key]) return;
                const parts = (name || '').trim().split(/\s+/);
                const firstName = parts[0] || undefined;
                const lastName = parts.slice(1).join(' ') || undefined;
                playerNames[key] = {
                  id: key,
                  firstName,
                  lastName,
                  role: role ? String(role).toLowerCase() : undefined,
                };
              };
              addExtraPerson(recordedBy, recordedByName, recordedByRole);
              addExtraPerson(assignedByMemberId || assignedById, assignedByName, assignedByRole);
              return {
                id: String(item.id || item.match_id || crypto.randomUUID()),
                courtNumber: item.courtNumber || item.court_number || 0,
                courtId: item.courtId ? String(item.courtId) : item.court_id ? String(item.court_id) : undefined,
                status,
                players,
                team1,
                team2,
                score: {
                  team1: Number((item.score && item.score.team1) ?? item.score_team1 ?? 0) || 0,
                  team2: Number((item.score && item.score.team2) ?? item.score_team2 ?? 0) || 0,
                },
                timestamp: item.timestamp || item.finished_at || item.started_at || new Date().toISOString(),
                startedAt: item.started_at || item.startedAt || item.timestamp,
                finishedAt: item.finished_at || item.finishedAt,
                durationSeconds: item.durationSeconds || item.duration_seconds,
                recordedBy,
                recordedByName,
                recordedByRole,
                assignedById,
                assignedByMemberId,
                assignedByName,
                assignedByRole,
                notes: typeof item.notes === 'string' && item.notes.trim() ? item.notes : undefined,
                equipmentUsed: Array.isArray(item.equipmentUsed || item.equipment_used)
                  ? (item.equipmentUsed || item.equipment_used).map((eq: any) => ({
                    memberId: String(eq.memberId || eq.member_id || ''),
                    shoes: eq.shoes,
                    racket: eq.racket,
                  }))
                  : [],
                isActive: status === 'ongoing',
                playerNames,
              };
            })
            : [];
          if (!historyForUi.length && mappedMemberHistory.length) {
            historyForUi = mappedMemberHistory;
          }
        } catch (err) {
          console.warn('Could not load member-specific match history, using club history instead', err);
        }
      }
      setMatchHistory(historyForUi);

      // Leaderboard (backend-computed, club-scoped)
      const rawLeaderboardItems = Array.isArray((clubLeaderboard as any)?.items)
        ? (clubLeaderboard as any).items
        : Array.isArray(clubLeaderboard)
          ? clubLeaderboard
          : [];

      if (Array.isArray(rawLeaderboardItems)) {
        const mappedLeaderboard: LeaderboardEntry[] = rawLeaderboardItems
          .map((item: any, idx: number) => {
            const id = String(item.id ?? item.member_id ?? '');
            if (!id) return null;
            const linkedMember = finalMembers.find((m) => m.id === id || m.userId === id);
            const firstName = item.first_name ?? item.firstName ?? linkedMember?.firstName ?? '';
            const lastName = item.last_name ?? item.lastName ?? linkedMember?.lastName ?? '';
            const role = (item.role ?? linkedMember?.role)?.toString().toLowerCase();
            return {
              id,
              firstName,
              lastName,
              role,
              played: Number(item.played ?? 0),
              won: Number(item.won ?? 0),
              lost: Number(item.lost ?? 0),
              scored: Number(item.scored ?? 0),
              against: Number(item.against ?? 0),
              pd: Number(item.pd ?? 0),
              pes: Number(item.pes ?? 0),
              mps: Number(item.mps ?? 0),
              confidence: Number(item.confidence ?? 0),
              rating: Number(item.rating ?? 0),
              hitCap: Boolean(item.hit_cap ?? item.hitCap),
              finalRank: Number(item.final_rank ?? item.finalRank ?? idx + 1),
            } as LeaderboardEntry;
          })
          .filter(Boolean) as LeaderboardEntry[];
        setLeaderboard(mappedLeaderboard);
      } else {
        setLeaderboard([]);
      }
      const ongoingMatches = mappedHistory.filter((m: any) => m.status === 'ongoing' || m.isActive);
      setMatches(ongoingMatches);

      // Merge in current user's equipment/evaluations for richer UI (admins included now)
      if (user) {
        const currentMemberIndex = finalMembers.findIndex(
          (m) =>
            m.userId === user.id ||
            m.id === user.id ||
            (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()),
        );
        if (currentMemberIndex >= 0) {
          const [shoes, rackets, feedback] = await Promise.allSettled([
            equipmentService.listShoes(),
            equipmentService.listRackets(),
            feedbackService.listForPlayer(user.id, currentClubId),
          ]);

          const equipment: Equipment[] = [
            ...(shoes.status === 'fulfilled' ? mapShoesToEquipment(shoes.value as any[]) : []),
            ...(rackets.status === 'fulfilled' ? mapRacketsToEquipment(rackets.value as any[]) : []),
          ];

          const evaluationItems: Evaluation[] =
            feedback.status === 'fulfilled'
              ? mapEvaluations(feedback.value as any[])
              : [];

          const updatedMember = {
            ...finalMembers[currentMemberIndex],
            equipment,
            evaluations: evaluationItems,
          };
          const mergedMembers = [...finalMembers];
          mergedMembers[currentMemberIndex] = updatedMember;
          setMembers(mergedMembers);
        }
      }

      // Re-apply active matches (from backend) to courts so UI stays in sync
      const updatedCourts = baseCourts.map((court) => {
        const activeMatch = ongoingMatches.find((m) => {
          if (m.courtId) return m.courtId === court.id;
          // fallback by number if id missing
          return m.courtNumber === court.number;
        });
        if (!activeMatch) return court;
        return {
          ...court,
          isActive: true,
          players: activeMatch.players,
          team1: activeMatch.team1,
          team2: activeMatch.team2,
          matchStartTime: activeMatch.timestamp,
        };
      });
      setCourts(updatedCourts);
    } catch (err) {
      console.error('Failed to refresh data', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      setHasLoadedInitial(true);
    }
  }, [activeClubId, user, hasLoadedInitial]);

  const scheduleRefresh = useCallback(
    (clubIdOverride?: number | null, options?: { light?: boolean }, delay = 300) => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
      }
      refreshTimerRef.current = window.setTimeout(() => {
        refreshTimerRef.current = null;
        refreshData(clubIdOverride, options);
      }, delay);
    },
    [refreshData],
  );

  const refreshQueueOnly = useCallback(async () => {
    if (!activeClubId) return;
    try {
      const queueList = (await queueService.list(activeClubId)) as ApiQueueEntry[];
      const mappedQueue = queueList.map(mapQueueEntry);
      setQueue(mappedQueue);

      const queuedIds = new Set(mappedQueue.filter((q) => q.status === 'QUEUED').map((entry) => entry.userId));
      const playingIds = new Set(mappedQueue.filter((q) => q.status === 'PLAYING').map((entry) => entry.userId));
      setMembers((prev) =>
        prev.map((member) => ({
          ...member,
          isAvailable: queuedIds.has(member.id),
          isPlaying: playingIds.has(member.id),
        })),
      );
    } catch (err) {
      console.error('Failed to refresh queue', err);
    }
  }, [activeClubId]);

  useEffect(() => {
    if (!user) {
      // Clear club data when logging out to avoid unauthorized calls
      setClub(null);
      setMembers([]);
      setCourts([]);
      setMatches([]);
      setMatchHistory([]);
      setLeaderboard([]);
      setQueue([]);
      setActiveClubId(null);
      setIsLoading(false);
      setIsRefreshing(false);
      setHasLoadedInitial(false);
      return;
    }
    if (user.needsConsent) {
      setClub(null);
      setMembers([]);
      setCourts([]);
      setMatches([]);
      setMatchHistory([]);
      setLeaderboard([]);
      setQueue([]);
      setActiveClubId(null);
      setIsLoading(false);
      setIsRefreshing(false);
      setHasLoadedInitial(true);
      return;
    }
    if (user.isSuperAdmin) {
      setClub(null);
      setMembers([]);
      setCourts([]);
      setMatches([]);
      setMatchHistory([]);
      setLeaderboard([]);
      setQueue([]);
      setActiveClubId(null);
      setIsLoading(false);
      setIsRefreshing(false);
      setHasLoadedInitial(true);
      return;
    }
    if (activeClubId === null) {
      const storedPreferred = Number(localStorage.getItem(preferredKey) || 0) || null;
      if (storedPreferred) {
        setActiveClubId(storedPreferred);
        return;
      } else if (!bootstrapClubRef.current) {
        bootstrapClubRef.current = true;
        (async () => {
          try {
            const clubs = (await clubService.listMine()) as Array<{ id?: number | string }>;
            const firstId = clubs?.[0]?.id ? Number(clubs[0].id) : null;
            if (firstId) {
              setActiveClubId(firstId);
              localStorage.setItem(preferredKey, String(firstId));
            } else {
              setIsLoading(false);
              setIsRefreshing(false);
              setHasLoadedInitial(true);
            }
          } catch (err) {
            console.error('Failed to load clubs for selection', err);
            setIsLoading(false);
            setIsRefreshing(false);
            setHasLoadedInitial(true);
          }
        })();
        return;
      }
    }
  }, [activeClubId, refreshData, user]);

  useEffect(() => {
    if (!user || user.isSuperAdmin || user.needsConsent) return;
    if (!activeClubId) return;
    if (lastFetchedClubRef.current === activeClubId) return;
    lastFetchedClubRef.current = activeClubId;
    refreshData(activeClubId);
  }, [activeClubId, refreshData, user]);

  // Realtime refresh moved to Queue & Courts page.

  const currentClubRole = React.useMemo(() => {
    if (!user) return null;
    const match = members.find(
      (m) =>
        m.id === String(user.id) ||
        m.userId === String(user.id) ||
        (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()),
    );
    return match?.role || null;
  }, [members, user]);

  const canManageCourts = () => currentClubRole === 'admin' || currentClubRole === 'coach';
  const canEvaluate = () => currentClubRole === 'admin' || currentClubRole === 'coach';

  const createClub = async (clubData: Omit<Club, 'id' | 'createdAt' | 'adminId'>) => {
    const mechanismMap: Record<string, string> = {
      'round-robin': 'ROUND_ROBIN',
      'skill-based': 'SKILL_BASED',
      'balanced': 'BALANCED_TEAM',
    };

    const payload = {
      name: clubData.name,
      sport_type: clubData.sportType,
      slogan: clubData.slogan,
      logo_url: clubData.logo,
      address: clubData.address,
      approx_players: clubData.approxPlayers,
      num_courts: clubData.numberOfCourts,
      selection_mechanism: mechanismMap[clubData.selectionMode] || 'ROUND_ROBIN',
      play_sessions: clubData.playTimes.map((ps: PlayTime) => ({
        day: DAY_NAME_TO_NUMBER[ps.day] || 1,
        start_time: ps.startTime,
        end_time: ps.endTime,
      })),
    };
    await clubService.create(payload);
    await refreshData();
  };

  const selectClub = async (clubId: number) => {
    setActiveClubId(clubId);
    localStorage.setItem(preferredKey, String(clubId));
  };

  const updateClub = async (updates: Partial<Club>) => {
    const cid = activeClubId || (club ? Number(club.id) : null);
    if (!cid) {
      toast.error(t('club_no_active_selected', 'No active club selected'));
      return;
    }
    const payload: Record<string, unknown> = {};
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.sportType !== undefined) payload.sport_type = updates.sportType;
    if (updates.slogan !== undefined) payload.slogan = updates.slogan;
    if (updates.logo !== undefined) payload.logo_url = updates.logo;
    if (updates.address !== undefined) payload.address = updates.address;
    if (updates.approxPlayers !== undefined) payload.approx_players = updates.approxPlayers;
    if (updates.numberOfCourts !== undefined) payload.num_courts = updates.numberOfCourts;
    if (updates.selectionMode !== undefined) {
      const mechanismMap: Record<string, string> = {
        'round-robin': 'ROUND_ROBIN',
        'skill-based': 'SKILL_BASED',
        'balanced': 'BALANCED_TEAM',
      };
      payload.selection_mechanism = mechanismMap[updates.selectionMode as string] || updates.selectionMode.toUpperCase();
    }
    if (updates.playTimes) {
      payload.play_sessions = updates.playTimes.map((ps) => ({
        day: DAY_NAME_TO_NUMBER[ps.day] || 1,
        start_time: ps.startTime,
        end_time: ps.endTime,
      }));
    }
    try {
      await clubService.update(cid, payload);
      await refreshData();
      toast.success(t('club_updated_simple', 'Club updated'));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(message || t('club_update_failed', 'Could not update club'));
      console.error('updateClub error', err);
      throw err;
    }
  };

  const addMember = async (
    memberData: Omit<Member, 'id' | 'createdAt' | 'equipment' | 'evaluations' | 'isAvailable' | 'isPlaying'>
  ) => {
    if (!activeClubId) throw new Error('No club selected');
    const res = await userService.adminAddMember({
      clubId: activeClubId,
      email: memberData.email,
      firstName: memberData.firstName,
      lastName: memberData.lastName,
      nickname: memberData.nickName ?? null,
      role: memberData.role,
    }) as AdminAddMemberResponse;

    await refreshData();
    return res;
  };

  const updateMember = async (id: string, updates: Partial<Member>) => {
    if (!activeClubId) return;
    const payload: Record<string, string | undefined> = {};
    if (updates.firstName !== undefined) payload.first_name = updates.firstName;
    if (updates.lastName !== undefined) payload.last_name = updates.lastName;
    if (updates.nickName !== undefined) payload.nickname = updates.nickName;
    if (updates.email !== undefined) payload.email = updates.email;

    if (Object.keys(payload).length) {
      await memberService.updateDetails(activeClubId, id, payload);
    }

    if (updates.role) {
      await memberService.updateRole(
        activeClubId,
        id,
        updates.role === 'coach' ? 'COACH' : 'PLAYER',
      );
    }

    scheduleRefresh(activeClubId, undefined, 300);
  };

  const updateMemberStatus = async (id: string, isActive: boolean) => {
    if (!activeClubId) return;
    await memberService.updateStatus(activeClubId, id, isActive);
    scheduleRefresh(activeClubId, undefined, 300);
  };

  const deleteMember = async (id: string) => {
    if (!activeClubId) return;
    await memberService.remove(activeClubId, id);
    scheduleRefresh(activeClubId, undefined, 300);
  };

  const getMemberById = (id: string) => members.find((m) => m.id === id || m.userId === id);

  const getMemberByUserId = (userId: string) => {
    return members.find(
      (m) => m.userId === userId || m.email?.toLowerCase() === (user?.email || '').toLowerCase(),
    );
  };

  const isCurrentUsersProfile = (memberId: string) => {
    if (!user) return false;
    if (user.id === memberId) return true;
    const target = members.find((m) => m.id === memberId || m.userId === memberId);
    if (!target || !target.email || !user.email) return false;
    return target.email.toLowerCase() === user.email.toLowerCase();
  };

  const addEquipment = async (memberId: string, equipment: Omit<Equipment, 'id' | 'addedAt'>) => {
    if (!user || !isCurrentUsersProfile(memberId)) return;
    if (equipment.type === 'shoes') {
      await equipmentService.addShoe({ make: equipment.make, manufacturer: equipment.manufacturer });
    } else {
      await equipmentService.addRacket({
        make: equipment.make,
        manufacturer: equipment.manufacturer,
        tension: equipment.tension ? String(equipment.tension) : undefined,
        string_type: equipment.stringType,
      });
    }
    scheduleRefresh(activeClubId, { light: true }, 400);
  };

  const updateEquipment = async (
    memberId: string,
    equipmentId: string,
    updates: Partial<Equipment>,
  ) => {
    if (!user || !isCurrentUsersProfile(memberId)) return;
    const target = members.find((m) => m.id === memberId)?.equipment.find((eq) => eq.id === equipmentId);
    if (!target) return;

    if (target.type === 'shoes') {
      await equipmentService.updateShoe(equipmentId, { make: updates.make, manufacturer: updates.manufacturer });
    } else {
      await equipmentService.updateRacket(equipmentId, {
        make: updates.make,
        manufacturer: updates.manufacturer,
        tension: updates.tension ? String(updates.tension) : undefined,
        string_type: updates.stringType,
      });
    }
    scheduleRefresh(activeClubId, { light: true }, 400);
  };

  const deleteEquipment = async (memberId: string, equipmentId: string) => {
    if (!user || !isCurrentUsersProfile(memberId)) return;
    const target = members.find((m) => m.id === memberId)?.equipment.find((eq) => eq.id === equipmentId);
    if (!target) return;
    if (target.type === 'shoes') {
      await equipmentService.deleteShoe(equipmentId);
    } else {
      await equipmentService.deleteRacket(equipmentId);
    }
    scheduleRefresh(activeClubId, { light: true }, 400);
  };

  const addEvaluation = async (memberId: string, evaluation: Omit<Evaluation, 'id' | 'date'>) => {
    if (!activeClubId) return;
    await feedbackService.submit({
      club_id: activeClubId,
      player_id: Number(memberId),
      technical_skills: evaluation.categories?.technical,
      movement: evaluation.categories?.movement,
      tactical_ability: evaluation.categories?.tactical,
      physical_attributes: evaluation.categories?.physical,
      psychological_factors: evaluation.categories?.psychological,
      quarter_plan: evaluation.quarterPlan || evaluation.feedback,
      comments: evaluation.feedback,
    });
    scheduleRefresh(activeClubId, undefined, 500);
  };

  const deleteEvaluation = async (memberId: string, evaluationId: string) => {
    if (!activeClubId) return;
    await feedbackService.delete(evaluationId);
    setMembers((prev) =>
      prev.map((member) =>
        member.id === memberId
          ? { ...member, evaluations: member.evaluations.filter((ev) => ev.id !== evaluationId) }
          : member,
      ),
    );
    scheduleRefresh(activeClubId, { light: true }, 400);
  };

  const canManageQueueEntry = (memberId: string) => {
    if (!user) return false;
    if (currentClubRole === 'admin' || currentClubRole === 'coach') return true;
    const currentMember = getMemberByUserId(user.id);
    if (!currentMember) return false;
    return currentMember.id === memberId || currentMember.userId === memberId;
  };

  const addToQueue = async (memberId: string) => {
    if (!activeClubId) return;
    await queueService.markAvailable(activeClubId, memberId);
    await refreshQueueOnly();
    scheduleRefresh(activeClubId, { light: true }, 400);
  };

  const removeFromQueue = async (memberId: string) => {
    if (!activeClubId) return;
    await queueService.remove(activeClubId, memberId);
    await refreshQueueOnly();
    scheduleRefresh(activeClubId, { light: true }, 400);
  };

  const clearQueue = async () => {
    if (!activeClubId) return;
    await queueService.clear(activeClubId);
    await refreshQueueOnly();
    scheduleRefresh(activeClubId, { light: true }, 400);
  };

  const shuffleQueue = async () => {
    if (!activeClubId) return;
    await queueService.shuffle(activeClubId);
    await refreshQueueOnly();
    scheduleRefresh(activeClubId, { light: true }, 400);
  };

  const setPlayerAvailable = async (memberId: string, available: boolean) => {
    if (!canManageQueueEntry(memberId)) {
      throw new Error('You can only manage your own queue status unless you are a coach or admin.');
    }

    if (available) {
      await addToQueue(memberId);
    } else {
      await removeFromQueue(memberId);
    }
  };

  const initializeCourts = async (count: number) => {
    await updateClub({ numberOfCourts: count });
  };

  const assignPlayersToCourt = async (courtId: string): Promise<AssignPlayersResult | null> => {
    if (!activeClubId) return null;
    if (courtId.startsWith('virtual')) {
      console.warn('Court IDs are not available from backend. Unable to assign players.');
      return null;
    }
    const result = (await courtService.assignPlayers(activeClubId, courtId)) as any;
    const team1 = (result.team1 || []).map((id: number | string) => String(id));
    const team2 = (result.team2 || []).map((id: number | string) => String(id));
    const courtNumber = courts.find((c) => c.id === courtId)?.number || 0;
    const resolvedCourtId = String(result.court_id ?? courtId);
    const response: AssignPlayersResult = {
      team1,
      team2,
      courtId: resolvedCourtId,
      matchId: result.match_id ? String(result.match_id) : undefined,
      roundRobinPool: Array.isArray(result.round_robin_pool)
        ? result.round_robin_pool.map((id: number | string) => String(id))
        : null,
      priorityCandidates: Array.isArray(result.priority_candidates)
        ? result.priority_candidates.map((id: number | string) => String(id))
        : null,
    };

    setCourts((prev) =>
      prev.map((c) =>
        c.id === courtId
          ? { ...c, isActive: true, players: [...team1, ...team2], team1, team2 }
          : c,
      ),
    );

    setMatches((prev) => [
      ...prev.filter((m) => (m as any).courtId !== courtId),
      {
        id: response.matchId || crypto.randomUUID(),
        courtNumber,
        players: [...team1, ...team2],
        team1,
        team2,
        score: { team1: 0, team2: 0 },
        timestamp: new Date().toISOString(),
        startedAt: new Date().toISOString(),
        recordedBy: user?.id || '',
        assignedById: user?.id,
        assignedByMemberId: user?.id,
        equipmentUsed: [],
        courtId: resolvedCourtId,
        isActive: true,
      } as Match & { courtId: string; isActive: boolean },
    ]);

    // Refresh queue status after assignment
    scheduleRefresh(activeClubId, undefined, 500);
    return response;
  };

  const finishMatch = async (
    courtId: string,
    score: { team1: number; team2: number },
    _equipmentUsed: Match['equipmentUsed'],
    notes?: string,
    _recordedBy?: string,
  ) => {
    const validation = validateBadmintonFinalScore(score.team1, score.team2);
    if (!validation.ok) {
      throw new Error(validation.message);
    }

    const activeMatch = matches.find((m: any) => (m as any).courtId === courtId);
    if (!activeMatch) {
      console.warn('No active match found for this court.');
      return;
    }

    const payload = {
      team1_score: String(score.team1),
      team2_score: String(score.team2),
      notes,
      recorded_by: user?.id,
      players: activeMatch.players.map((pid: string) => {
        const equipment = (_equipmentUsed || []).find((eq) => eq.memberId === pid);
        return {
          player_id: Number(pid),
          racket_id: equipment?.racket ? Number(equipment.racket) : undefined,
          shoe_id: equipment?.shoes ? Number(equipment.shoes) : undefined,
        };
      }),
    };

    await matchService.finish(activeMatch.id, payload);

    // Persist finished match to local history
    setMatchHistory((prev) => [
      ...prev,
      {
        ...activeMatch,
        score: { team1: score.team1, team2: score.team2 },
        notes,
        timestamp: new Date().toISOString(),
        recordedBy: user?.id || '',
        recordedByName: user ? `${user.firstName} ${user.lastName}`.trim() : undefined,
        equipmentUsed: _equipmentUsed || [],
        isActive: false,
      },
    ]);

    setMatches((prev) => prev.filter((m: any) => (m as any).courtId !== courtId));
    setCourts((prev) =>
      prev.map((c) =>
        c.id === courtId ? { ...c, isActive: false, players: [], team1: [], team2: [] } : c,
      ),
    );
    scheduleRefresh(activeClubId, undefined, 500);
  };

  return (
    <ClubContext.Provider
      value={{
        club,
        isLoading,
        hasLoadedInitial,
        isRefreshing,
        activeClubId,
        currentClubRole,
        members,
        courts,
        matches,
        matchHistory,
        leaderboard,
        queue,
        refreshData,
        selectClub,
        createClub,
        updateClub,
        addMember,
        updateMember,
        deleteMember,
        getMemberById,
        getMemberByUserId,
        updateMemberStatus,
        addEquipment,
        updateEquipment,
        deleteEquipment,
        addEvaluation,
        deleteEvaluation,
        addToQueue,
        removeFromQueue,
        clearQueue,
        shuffleQueue,
        setPlayerAvailable,
        initializeCourts,
        assignPlayersToCourt,
        finishMatch,
        canManageCourts,
        canEvaluate,
      }}
    >
      {children}
    </ClubContext.Provider>
  );
}

export function useClub() {
  const context = useContext(ClubContext);
  if (!context) {
    throw new Error('useClub must be used within ClubProvider');
  }
  return context;
}
